// apps/web/src/components/layout/SortableList.tsx
//
// Phase 9 Plan 11 Task 1 — SortableList wrapper around @dnd-kit primitives.
//
// Shape per Plan 09-11 must_haves §"truths":
//   - PointerSensor activationConstraint distance:4  (pitfall 5 mitigation —
//     drag fires only after 4px of pointer movement, NOT on click). Without
//     this, every click on a row inside the list would trigger a drag start,
//     trampling the user's actual click intent.
//   - KeyboardSensor coordinateGetter sortableKeyboardCoordinates (Mission
//     Critical accessibility — UI-SPEC threat T-09-11-03).
//   - DndContext + SortableContext composition with verticalListSortingStrategy.
//   - onReorder callback invoked from handleDragEnd with (oldIdx, newIdx).
//
// Pitfall 5 — `PointerSensor activationConstraint distance:4`: the documented
// fix from RESEARCH §"Drag-and-Drop Reorder" (lines 2058-2095). Without it the
// outer drag-handle button's onClick handlers are eaten by sensor activation.
//
// `buildSortableSensorsConfig` is exported as a pure-data factory so the
// colocated jsdom test can assert the sensor configuration without running
// @dnd-kit's sensor machinery (which is hard to drive in jsdom). The same
// factory data is consumed by the component below at render time via the
// `useSensors` hook + `useSensor` wrappers.
//
// SANITIZATION: no inline styles in JSX here; no user-string interpolation.
// The handleDragEnd path performs purely numeric oldIdx/newIdx computation
// from the dnd-kit DragEndEvent.active.id + over.id strings (compared against
// the items[].id list provided by the caller).
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type * as React from "react";

export interface SortableListItem {
  id: string;
}

export interface SortableListProps {
  items: ReadonlyArray<SortableListItem>;
  onReorder: (oldIdx: number, newIdx: number) => void;
  children: React.ReactNode;
}

// Pure-data sensor configuration descriptor — exported so the colocated test
// can assert the wiring without invoking the dnd-kit sensor runtime. The
// component below converts this list into useSensor(...) calls inside the
// component body.
export interface SortableSensorConfig {
  kind: "pointer" | "keyboard";
  activationConstraint?: { distance: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coordinateGetter?: (...args: any[]) => unknown;
}

export function buildSortableSensorsConfig(): ReadonlyArray<SortableSensorConfig> {
  return [
    { kind: "pointer", activationConstraint: { distance: 4 } },
    { kind: "keyboard", coordinateGetter: sortableKeyboardCoordinates },
  ];
}

export function SortableList(props: SortableListProps): React.ReactElement {
  // Sensors are wired per the buildSortableSensorsConfig() shape; we call the
  // hooks directly here so React preserves stable hook order across renders.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (over === null) return;
    if (active.id === over.id) return;
    const ids = props.items.map((i) => i.id);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    props.onReorder(oldIdx, newIdx);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={props.items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <div data-testid="sortable-list">{props.children}</div>
      </SortableContext>
    </DndContext>
  );
}
SortableList.displayName = "SortableList";
